import { useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { DateTime } from 'luxon';

import { calendarFormatProps } from '../CalendarProps';
import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { composeVariant } from './buttonClasses';
import { popoverBorder } from './CalendarPopover';
import type { Size } from './types';
import { sizeRounded } from './types';

export type CalendarRangePopoverProps = {
  popoverId: string;
  anchorName: string;
  size?: Size;
  /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft'`. Defaults to `'filled'`. */
  tone?: Style;
  startValue?: number; // Timestamp in ms
  endValue?: number; // Timestamp in ms
  onRangeChange?: (start: string, end: string) => void; // both "yyyy-MM-dd"
  onOpenChange?: (open: boolean) => void;
};

export default function CalendarRangePopover({
  popoverId,
  anchorName,
  size = 'md',
  color,
  tone,
  startValue,
  endValue,
  onRangeChange,
  onOpenChange,
}: CalendarRangePopoverProps) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Scoped style overrides for THIS popover only (`#popoverId` raises specificity above the
  // app's global `.react-calendar` rules, e.g. nextjs global.scss's `width: 280px !important`
  // + `border-radius: 50%`). Two things:
  //   1. Let the two-month (doubleView) layout keep its full width instead of being clamped/clipped.
  //   2. Render the selected/hovered range as one connected path (flat fill, pill-rounded ends)
  //      instead of a row of separate ovals.
  //   3. Hide the leading/trailing days that belong to the adjacent month (a fixed 6-week grid
  //      keeps both months the same height; we just don't show the other month's dates).
  const scopedCss = `
    #${popoverId} .react-calendar--doubleView{width:700px!important;max-width:none!important;overflow:visible!important}
    #${popoverId} .react-calendar__month-view__days__day--neighboringMonth{visibility:hidden!important}
    #${popoverId} .react-calendar__tile--range,
    #${popoverId} .react-calendar__tile--hover{border-radius:0!important}
    #${popoverId} .react-calendar__tile--rangeStart,
    #${popoverId} .react-calendar__tile--hoverStart{border-radius:9999px 0 0 9999px!important}
    #${popoverId} .react-calendar__tile--rangeEnd,
    #${popoverId} .react-calendar__tile--hoverEnd{border-radius:0 9999px 9999px 0!important}
    #${popoverId} .react-calendar__tile--rangeBothEnds,
    #${popoverId} .react-calendar__tile--hoverBothEnds{border-radius:9999px!important}
  `;

  // Normalize to start-of-day / end-of-day so BOTH endpoints are fully within the value range.
  // Otherwise react-calendar marks the end day `--hasActive` (a lighter shade) instead of
  // `--active`, leaving the range bar a different color at the end.
  const startDate = startValue && !isNaN(startValue) ? DateTime.fromMillis(startValue).startOf('day').toJSDate() : null;
  const endDate = endValue && !isNaN(endValue) ? DateTime.fromMillis(endValue).endOf('day').toJSDate() : null;
  const rangeValue = startDate && endDate ? ([startDate, endDate] as [Date, Date]) : undefined;

  function handleRangeChange(start: Date, end: Date) {
    if (onRangeChange) {
      onRangeChange(DateTime.fromJSDate(start).toFormat('yyyy-MM-dd'), DateTime.fromJSDate(end).toFormat('yyyy-MM-dd'));
    }
    popoverRef.current?.hidePopover();
  }

  return (
    <div
      ref={popoverRef}
      id={popoverId}
      popover="auto"
      onToggle={(e) => {
        const open = (e as React.ToggleEvent<HTMLDivElement>).newState === 'open';
        setIsOpen(open);
        onOpenChange?.(open);
      }}
      style={{
        position: 'fixed',
        positionAnchor: anchorName,
        positionArea: 'block-end span-inline-end',
        positionTryFallbacks: 'flip-block, flip-inline, flip-block flip-inline',
        justifySelf: 'start',
        margin: 0,
        inset: 'auto',
        // Keep the two-month view on screen by scrolling instead of being clipped
        // on viewports too narrow for both months.
        maxWidth: 'calc(100vw - 1rem)',
        overflowX: 'auto',
      } as unknown as React.CSSProperties}
      className={cn(
        'p-0 border bg-white dark:bg-charade-950 shadow-lg',
        popoverBorder(effectiveVariant),
        sizeRounded(size),
        !isOpen && 'hidden',
      )}>
      <style>{scopedCss}</style>
      <Calendar
        selectRange
        showDoubleView
        // End-of-day for the range end so the just-completed end day reads as `--active`
        // (same bar color) rather than the lighter `--hasActive`.
        returnValue="range"
        value={rangeValue}
        {...calendarFormatProps}
        onChange={(value) => {
          if (Array.isArray(value)) {
            const [start, end] = value;
            if (start && end) {
              handleRangeChange(start, end);
            }
          }
        }}
      />
    </div>
  );
}
